import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useScript } from "@hooks/useScript";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { usePOAStore } from "@store/poaStore/poaStore";
import { useSubmitPOADokobitConfirmMutation } from "@services/powersOfAttorney/mutations";
import { type IPOADokobitConsentResponse } from "@services/powersOfAttorney/types";
import { waitForVar } from "@utils/waitForVar";
import { DOKOBIT_SCRIPT_URL } from "@/constants";

interface IDokobitSignModalProps extends IUseModalProps {
  dokobitData: IPOADokobitConsentResponse;
  onSuccess?: () => void;
  modalStack: number[];
}

interface IDokobitScriptInterface {
  PostMessage: {
    init: () => void;
  };
  onSignSuccess: () => Promise<void> | void;
}

export const DokobitSignModal: React.FC<IDokobitSignModalProps> = (props) => {
  const { dokobitData, onSuccess, modalStack, ...rest } = props;
  const modal = useModal(rest);
  const { close } = useModalStore();
  const { providerList, reset } = usePOAStore();
  const { t } = useTranslation();
  useScript(DOKOBIT_SCRIPT_URL);

  const { mutateAsync: confirmPOADokobitConsent } =
    useSubmitPOADokobitConfirmMutation();

  const { toastApiError } = useApiErrorUtils();

  const handleDokobitSuccess = useCallback(async () => {
    const flatProviderList = Object.entries(providerList).flatMap(
      ([jointBillServiceId, providers]) =>
        providers
          .filter((provider) => provider.selected)
          .map((provider) => ({
            jointBillServiceId: Number(jointBillServiceId),
            serviceProviderId: provider.serviceProviderId,
            companyCode: provider.companyCode,
            serviceProviderName: provider.serviceProviderName,
          })),
    );

    const response = await confirmPOADokobitConsent({
      signingToken: dokobitData.signingToken,
      requestedServiceProviders: flatProviderList,
    }).catch(toastApiError);

    if (!response) {
      return;
    }

    reset();
    toast.success(t("dokobitSignModal.success"));
    onSuccess?.();

    modalStack.forEach((modalId) => {
      close(modalId);
    });
    modal.close();
  }, [
    t,
    close,
    confirmPOADokobitConsent,
    dokobitData.signingToken,
    modal,
    modalStack,
    onSuccess,
    providerList,
    reset,
    toastApiError,
  ]);

  useEffect(() => {
    const initDokobit = async () => {
      const Isign = (await waitForVar("Isign")) as IDokobitScriptInterface;

      Isign.PostMessage.init();
      Isign.onSignSuccess = handleDokobitSuccess;
    };

    void initDokobit();
  }, [handleDokobitSuccess]);

  return (
    <Modal modal={modal} className="md:max-w-3xl">
      <iframe
        id="isign-gateway"
        width="100%"
        height="780px"
        src={dokobitData.iframeUrl}
      />
    </Modal>
  );
};
